import { type CSSProperties, type RefObject, useEffect, useState } from 'react';

const FADE_PX = 24;

/*
    Fades the content out at each edge that the user can scroll toward. A mask, not a white overlay, so
    the fade blends into any background. StudentNetworkV2's ScrollFadeContainer does the same vertically.
*/
export function useHorizontalScrollFade(ref: RefObject<HTMLElement | null>, contentKey: string): CSSProperties {
    const [fade, setFade] = useState({ start: false, end: false });

    useEffect(() => {
        const element = ref.current;
        if (!element) return undefined;

        const update = () => {
            // scrollLeft is negative in RTL, so compare its absolute value
            const scrolled = Math.abs(element.scrollLeft);
            const maxScroll = element.scrollWidth - element.clientWidth;
            setFade({ start: scrolled > 1, end: maxScroll - scrolled > 1 });
        };

        update();
        element.addEventListener('scroll', update, { passive: true });
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => {
            element.removeEventListener('scroll', update);
            observer.disconnect();
        };
    }, [ref, contentKey]);

    if (!fade.start && !fade.end) return {};

    // `to right` follows the physical direction. In RTL the scroll start is on the right.
    const isRtl = ref.current ? getComputedStyle(ref.current).direction === 'rtl' : false;
    const left = isRtl ? fade.end : fade.start;
    const right = isRtl ? fade.start : fade.end;
    const mask = `linear-gradient(to right, transparent, black ${left ? FADE_PX : 0}px, black calc(100% - ${
        right ? FADE_PX : 0
    }px), transparent)`;

    return { maskImage: mask, WebkitMaskImage: mask };
}
